import {Nullable} from '../../app/TypeHelpers'
import {UNIT_OF_BUSINESS_SCHEDULE, VALID_OR_NOT} from '../../CodeMaster'
import moment from 'moment'
import Time from '../../dateHelpers/Time'
import {BusinessScheduleWithDateTime} from './ReserveFacilityPaymentModel'
import {BusinessSchedule} from '../facilities/FacilityForReservation'

export default class PaymentModelSchedulePreProcessor {

    static splitAllDaysBusinessSchedulesByDayOfWeeks(
        businessSchedules: BusinessSchedule[],
    ): BusinessSchedule[] {
        const nonNullBusinessSchedules = businessSchedules
            .map(schedule => {
                return {
                    ...schedule,
                    dateStraddle: schedule.dateStraddle || VALID_OR_NOT.NOT,
                }
            })

        // noinspection UnnecessaryLocalVariableJS
        const splitSchedulesByAllDayOfWeeks = nonNullBusinessSchedules
            .map(schedule => {
                if (schedule.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS) {
                    return schedule
                }
                const targetDayOfWeeks: UNIT_OF_BUSINESS_SCHEDULE[] = [
                    UNIT_OF_BUSINESS_SCHEDULE.SUNDAY,
                    UNIT_OF_BUSINESS_SCHEDULE.MONDAY,
                    UNIT_OF_BUSINESS_SCHEDULE.TUESDAY,
                    UNIT_OF_BUSINESS_SCHEDULE.WEDNESDAY,
                    UNIT_OF_BUSINESS_SCHEDULE.THURSDAY,
                    UNIT_OF_BUSINESS_SCHEDULE.FRIDAY,
                    UNIT_OF_BUSINESS_SCHEDULE.SATURDAY,
                ]
                return targetDayOfWeeks
                    .map(dayOfWeek => ({...schedule, businessScheduleUnit: dayOfWeek}))
            })
            .flat()

        return splitSchedulesByAllDayOfWeeks
    }

    static updateBusinessSchedulesDependOnTargetDate(
        businessSchedules: BusinessSchedule[],
        holidays: Nullable<string[]>,
        requestStartDateTimeMoment: moment.Moment,
        requestEndDateTimeMoment: moment.Moment,
    ): BusinessScheduleWithDateTime[] {
        if (holidays === null) {
            return []
        }
        if (requestEndDateTimeMoment.diff(requestStartDateTimeMoment) < 0) {
            return []
        }

        const targetBusinessSchedules: BusinessScheduleWithDateTime[] =
            filterBusinessSchedules(
                businessSchedules,
                holidays,
                requestStartDateTimeMoment,
                requestEndDateTimeMoment,
            )

        const targetPeriodIsValid: boolean =
            includedInSomeBusinessSchedules(
                targetBusinessSchedules,
                requestStartDateTimeMoment,
                requestEndDateTimeMoment,
            )

        if (!targetPeriodIsValid) {
            return []
        }

        const isNotSequentialSchedules = targetBusinessSchedules
            .some((schedule, index, array) => {
                const isLastOfArray: boolean = (index === array.length - 1)
                if (isLastOfArray) {
                    return false
                }
                const nextScheduleStart = array[index + 1].startDateMoment
                return (schedule.endDateMoment.diff(nextScheduleStart) !== 0)
            })
        return isNotSequentialSchedules ? [] : targetBusinessSchedules
    }
}

const convertToBusinessScheduleWithDateTime = (
    targetDay: moment.Moment,
    schedule: BusinessSchedule,
): BusinessScheduleWithDateTime => {
    if (schedule.startTime === null || schedule.endTime === null) {
        throw Error('(schedule.startTime === null || schedule.endTime === null)')
    }
    const startTimeObject: Time = Time.fromString(schedule.startTime)
    const endTimeObject: Time = Time.fromString(schedule.endTime)

    const {startTime, endTime, dateStraddle, joinPrevDaysSchedule, charge, chargeUnit, ...rest} = schedule

    const startDateTime = targetDay.clone().startOf('date').add(startTimeObject.hours * 60 + startTimeObject.minutes, 'minutes')
    const endDateTime = targetDay.clone().startOf('date').add(endTimeObject.hours * 60 + endTimeObject.minutes, 'minutes')
    if (schedule.dateStraddle === VALID_OR_NOT.VALID) {
        endDateTime.add(1, 'day')
    }
    return {
        ...rest,
        dateStraddle: dateStraddle || VALID_OR_NOT.NOT,
        joinPrevDaysSchedule: joinPrevDaysSchedule || VALID_OR_NOT.NOT,
        charge: charge,
        chargeUnit: chargeUnit,
        startDateMoment: startDateTime,
        endDateMoment: endDateTime,
    }
}

const targetTimeIsIncludedInSomeSchedules = (
    businessSchedulesWithDateTime: BusinessScheduleWithDateTime[],
    targetMoment: moment.Moment,
): boolean => {
    // noinspection UnnecessaryLocalVariableJS
    const targetIsInsideOfSomeBusinessSchedules =
        businessSchedulesWithDateTime.some(schedule => {
            return schedule.startDateMoment <= targetMoment
                && targetMoment <= schedule.endDateMoment
        })
    return targetIsInsideOfSomeBusinessSchedules
}

const includedInSomeBusinessSchedules = (
    schedules: BusinessScheduleWithDateTime[],
    startDateTimeMoment: moment.Moment,
    endDateTimeMoment: moment.Moment,
): boolean => {
    const startIsOk = targetTimeIsIncludedInSomeSchedules(schedules, startDateTimeMoment)
    if (!startIsOk) {
        return false
    }

    const endIsOk = targetTimeIsIncludedInSomeSchedules(schedules, endDateTimeMoment)
    // noinspection RedundantIfStatementJS
    if (!endIsOk) {
        return false
    }

    return true
}

const filterBusinessSchedulesByPeriod = (
    businessSchedulesWithDateTime: BusinessScheduleWithDateTime[],
    startDateTime: moment.Moment,
    endDateTime: moment.Moment,
): BusinessScheduleWithDateTime[] => {
    return [...businessSchedulesWithDateTime]
        .sort((a, b) => a.startDateMoment.diff(b.startDateMoment))
        .filter(schedule => !(
            endDateTime <= schedule.startDateMoment
            || schedule.endDateMoment <= startDateTime
        ))
}

const findTargetDayOfWeek = (targetMoment: moment.Moment): UNIT_OF_BUSINESS_SCHEDULE => {
    const durationDayOfWeek: number = targetMoment.isoWeekday() % 7 + 1
    return UNIT_OF_BUSINESS_SCHEDULE.codeToEnumConstants(`${durationDayOfWeek}`)
}

const convertToBusinessSchedulesWithDateTime = (
    businessSchedules: BusinessSchedule[],
    holidays: string[],
    targetDays: moment.Moment[],
): BusinessScheduleWithDateTime[] => {
    const hasHolidayBusinessSchedule: boolean =
        businessSchedules.some(schedule => UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY === schedule.businessScheduleUnit)

    return targetDays.map(targetDay => {
        const isHoliday: boolean = holidays.some(holiday => targetDay.isSame(holiday))
        const validHolidaySchedule = isHoliday && hasHolidayBusinessSchedule

        return businessSchedules
            .filter(schedule => validHolidaySchedule ?
                UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY === schedule.businessScheduleUnit :
                findTargetDayOfWeek(targetDay) === schedule.businessScheduleUnit)
            .map(schedule => convertToBusinessScheduleWithDateTime(targetDay, schedule))
    }).flat()
}

const joinPrevBusinessSchedule = (
    businessSchedules: BusinessScheduleWithDateTime[],
): BusinessScheduleWithDateTime[] => {
    return [...businessSchedules]
        .sort((a, b) => a.startDateMoment.diff(b.startDateMoment))
        .reduce((schedules: BusinessScheduleWithDateTime[], schedule) => {
            if (schedule.joinPrevDaysSchedule === VALID_OR_NOT.VALID) {
                const startOfJoinedSchedule = schedules.pop()
                if (startOfJoinedSchedule) {
                    schedules.push(
                        {
                            ...startOfJoinedSchedule,
                            endDateMoment: schedule.endDateMoment,
                        },
                    )
                }
            } else {
                schedules.push(schedule)
            }
            return schedules
        }, [])
}

const filterBusinessSchedules = (
    businessSchedules: BusinessSchedule[],
    holidays: string[],
    startDateTime: moment.Moment,
    endDateTime: moment.Moment,
): BusinessScheduleWithDateTime[] => {
    const targetStartDay = startDateTime.clone().startOf('day').add(-1, 'week')
    const targetEndDay = endDateTime.clone().startOf('day')

    const targetDaysCount = targetEndDay.diff(targetStartDay, 'day') + 1
    const targetDays = new Array(targetDaysCount)
        .fill(null)
        .map((_, dayStep) => {
            return targetStartDay.clone().add(dayStep, 'day')
        })

    const schedulesWithDateTime = convertToBusinessSchedulesWithDateTime(businessSchedules, holidays, targetDays)

    const joinedSchedules = joinPrevBusinessSchedule(schedulesWithDateTime)

    return filterBusinessSchedulesByPeriod(
        joinedSchedules,
        startDateTime,
        endDateTime,
    )
}
